import { createRouter, createWebHistory } from 'vue-router'
import SignIn from '../components/auth/SignIn.vue'
import SignUp from '../components/auth/SignUp.vue'
import ForgotPassword from '../components/auth/ForgotPassword.vue'
import Home from '../views/Home.vue'


const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/signin',
    name: 'SignIn',
    component: SignIn
  },
  {
    path: '/signup',
    name: 'SignUp',
    component: SignUp
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: () => import('@/views/admin/AdminDashboard.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/faculty',
    name: 'AdminFaculty',
    component: () => import('@/views/admin/AdminFaculty.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/faculty/:id',
    name: 'AdminFacultyProfile',
    component: () => import('@/views/admin/AdminFacultyProfile.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/extensions',
    name: 'AdminExtensions',
    component: () => import('@/views/admin/AdminExtensions.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/account',
    name: 'AdminAccount',
    component: () => import('@/views/admin/AdminAccount.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/projects',
    name: 'AdminProject',
    component: () => import('@/views/admin/AdminProjects.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/reports',
    name: 'AdminReports',
    component: () => import('@/views/admin/AdminReports.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/research',
    name: 'AdminResearch',
    component: () => import('@/views/admin/AdminResearch.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/faculty/dashboard',
    name: 'FacultyDashboard',
    component: () => import('@/views/faculty/FacultyDashboard.vue'),
    meta: { requiresAuth: true, role: 'faculty' }
  },
  {
    path: '/faculty/research',
    name: 'FacultyResearch',
    component: () => import('@/views/faculty/FacultyResearch.vue'),
    meta: { requiresAuth: true, role: 'faculty' }
  },
  {
    path: '/faculty/projects',
    name: 'FacultyProjects',
    component: () => import('@/views/faculty/FacultyProject.vue'),
    meta: { requiresAuth: true, role: 'faculty' }
  },
  {
    path: '/faculty/extensions',
    name: 'FacultyExtensions',
    component: () => import('@/views/faculty/FacultyExtension.vue'),
    meta: { requiresAuth: true, role: 'faculty' }
  },
  {
    path: '/faculty/account',
    name: 'FacultyAccount',
    component: () => import('@/views/faculty/FacultyAccount.vue'),
    meta: { requiresAuth: true, role: 'faculty' }
  },
  {
    path: '/faculty/report',
    name: 'FacultyReport',
    component: () => import('@/views/faculty/FacultyReports.vue'),
    meta: { requiresAuth: true, role: 'faculty' }
  },
  {
    path: '/admin/upload',
    name: 'AdminExcelUpload',
    component: () => import('@/views/admin/AdminExcelUpload.vue'),
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

function isAccountApproved(user) {
  if (!user || typeof user !== 'object') return false

  if (Object.prototype.hasOwnProperty.call(user, 'approved')) return !!user.approved
  if (Object.prototype.hasOwnProperty.call(user, 'is_approved')) return !!user.is_approved
  if (Object.prototype.hasOwnProperty.call(user, 'isApproved')) return !!user.isApproved

  const rawStatus = (user.account_status ?? user.status ?? '').toString().trim().toLowerCase()
  if (rawStatus) {
    const blockedStatuses = ['pending', 'for approval', 'unapproved', 'rejected', 'inactive', 'disabled']
    if (blockedStatuses.includes(rawStatus)) return false
  }

  return true
}

// Navigation Guard: Check auth before entering routes
router.beforeEach((to, from, next) => {
  const user = JSON.parse(localStorage.getItem('user'));
  const isAuthenticated = user !== null;
  const approved = isAuthenticated ? isAccountApproved(user) : false;

  if (isAuthenticated && !approved) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    if (to.path !== '/signin') {
      alert('Your account is not yet approved.');
    }
    next('/signin');
    return;
  }

  // Route requires auth
  if (to.meta.requiresAuth && !isAuthenticated) {
    next('/signin');
  } 
  // Route requires auth and has role restriction
  else if (to.meta.requiresAuth && isAuthenticated) {
    if (to.meta.role && to.meta.role !== user.role) {
      // Redirect users to their dashboard if role mismatch
      if (user.role === 'admin') next('/admin/dashboard');
      else if (user.role === 'faculty') next('/faculty/dashboard');
      else next('/signin');
    } else {
      next();
    }
  } 
  // Logged-in user tries to access SignIn or SignUp
  else if ((to.path === '/signin' || to.path === '/signup') && isAuthenticated) {
    if (user.role === 'admin') next('/admin/dashboard');
    else if (user.role === 'faculty') next('/faculty/dashboard');
    else next('/signin');
  } 
  else {
    next();
  }
});

export default router
